import { forwardRef, useEffect, useId, useRef } from 'react'
import type { MouseEvent, ReactNode } from 'react'
import { cx } from './cx'

export interface CheckboxProps {
  readonly id?: string
  readonly checked: boolean
  readonly onChange: (checked: boolean) => void
  /** ReactNode (not just a string) so a label can embed inline links (e.g.
   * checkout's terms acknowledgment, UX spec §2.4/§4.4) — those links stay
   * independently clickable without also toggling the checkbox. This
   * primitive itself guards that (see the wrapping `<label>`'s `onClick`
   * below) rather than requiring every caller to remember to
   * `stopPropagation` on its own nested links. */
  readonly label: ReactNode
  readonly disabled?: boolean
  readonly required?: boolean
  readonly className?: string
  /**
   * UID-600 — tri-state ("some but not all selected") rendering for
   * select-all checkboxes, e.g. the STEP/PDF staging page's "Select all
   * identified/matched parts" controls. Sets the underlying native
   * `<input>`'s `.indeterminate` DOM PROPERTY (there is no matching HTML
   * attribute/React prop — it must be assigned imperatively) and drives
   * `aria-checked` explicitly (`'mixed'` while indeterminate, else the
   * literal `checked` boolean) — matching the `aria-checked={indeterminate
   * ? 'mixed' : checked}` convention every other hand-rolled tri-state
   * checkbox in this app already follows (e.g. `RfqPartsCard.tsx`'s
   * `bulkSelectAll`). Purely a rendering concern: `checked` still drives the
   * control's actual boolean state and what `onChange` reports;
   * `indeterminate` never changes what gets toggled, only how the tri-state
   * is DISPLAYED (visually and to AT) while some-but-not-all rows are
   * selected.
   *
   * Left `undefined` (the default) for an ordinary two-state checkbox —
   * `aria-checked` then stays UNSET, same as before this prop existed, so
   * every existing caller that never passes it is byte-for-byte unaffected.
   */
  readonly indeterminate?: boolean
  /**
   * UID-600 (bug follow-up) — raw-click passthrough for per-row STEP/PDF
   * staging checkboxes (`IdentifiedPartRow`/`MatchedPartRow`). Those rows
   * need shift-click range-select, which reads `event.shiftKey` — a field
   * only a `MouseEvent` carries; React's `ChangeEvent` for a checkbox has no
   * modifier-key fields at all (confirmed both at runtime and via `tsc`'s
   * own TS2339 on `.shiftKey`, see `StagingPage.bom-hierarchy.test.tsx`'s
   * "selection order follows the unified list's render order" describe
   * block). So the toggle itself is wired through `onClick`, not `onChange`
   * — `onChange` here stays required and still fires (kept in sync with
   * `checked` for every other caller), but a row that needs the click event
   * passes `onClick` too and does its own state update from it, exactly as
   * `StagingBomLeafRow`/the select-all headers' `onChange`-only callers are
   * unaffected (this prop is optional and additive).
   *
   * Left `undefined` (the default) for ordinary two-state, change-driven
   * checkboxes — every existing caller that never passes it is
   * byte-for-byte unaffected.
   */
  readonly onClick?: (event: MouseEvent<HTMLInputElement>) => void
}

/**
 * Binary checkbox primitive (components.md "Checkbox" —
 * unchecked/checked/disabled/`:focus-visible`). First real consumers: UID-60
 * checkout's terms acknowledgment + "save card" affordance. Always a real
 * `<label>` with click-through on the full label text, not just the box.
 *
 * Forwards its ref to the underlying `<input>` — UID-60 moves focus onto the
 * terms checkbox when "Place order" is activated while unchecked (UX spec
 * §2.4) or when the server rejects with `terms_not_accepted` (§3.5).
 */
export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(function Checkbox(
  { id, checked, onChange, label, disabled, required, className, indeterminate, onClick },
  forwardedRef,
) {
  const generatedId = useId()
  const checkboxId = id ?? generatedId

  // `.indeterminate` has no HTML attribute / React prop counterpart — it
  // must be assigned imperatively on the DOM node, so this component needs
  // its OWN ref regardless of whatever ref a caller forwards in. `setRefs`
  // below fans the node out to both.
  const internalRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (internalRef.current) {
      internalRef.current.indeterminate = Boolean(indeterminate)
    }
  }, [indeterminate])

  function setRefs(node: HTMLInputElement | null) {
    internalRef.current = node
    if (typeof forwardedRef === 'function') {
      forwardedRef(node)
    } else if (forwardedRef) {
      forwardedRef.current = node
    }
  }

  // Clicking a nested interactive element (e.g. the terms checkbox's "Terms
  // of Sale" link, UX spec §2.4) must activate THAT link, not also toggle
  // the checkbox via the wrapping <label>'s native click-forwarding
  // behavior. Real browsers already exclude clicks on nested
  // labelable/interactive descendants from that forwarding, but that
  // exclusion is keyed off the native click's target — it can be defeated
  // if a descendant's OWN click handler calls `stopPropagation()` (which,
  // under React's synthetic event delegation, stops this ancestor handler
  // from ever running at all if registered on `onClick`). Using
  // `onClickCapture` instead runs this guard during the CAPTURE phase,
  // before the nested link's own bubble-phase handler executes, so it can't
  // be skipped by anything the link's handler does.
  function handleLabelClickCapture(event: MouseEvent<HTMLLabelElement>) {
    if (event.target instanceof Element && event.target.closest('a,button')) {
      event.preventDefault()
    }
  }

  return (
    <div className={cx('checkbox-field', className)}>
      <label
        htmlFor={checkboxId}
        className="checkbox-label"
        onClickCapture={handleLabelClickCapture}
      >
        <input
          ref={setRefs}
          id={checkboxId}
          type="checkbox"
          className="checkbox-input"
          checked={checked}
          disabled={disabled}
          aria-required={required || undefined}
          aria-checked={indeterminate === undefined ? undefined : indeterminate ? 'mixed' : checked}
          onClick={onClick}
          onChange={(event) => onChange(event.target.checked)}
        />
        <span className="checkbox-label-text">{label}</span>
      </label>
    </div>
  )
})
